// "Build your ecommerce data stack" quiz: questions, per-layer scoring, the starting phase, and URL helpers.
//
// Same approach as toolQuizzes.js, applied once per layer of the stack. Each answer gives points to
// picks in one or more layers. Layers are decided in a fixed order, and a pick can require an earlier
// layer's pick or an answer (Snowflake semantic views need Snowflake, for example). Within a layer the
// pick with the most points wins; ties go to the pick listed first, which is the simpler, cheaper option.
// Reasons come from the answers that gave the winning pick the most points.

import { answersFromSearch as quizAnswersFromSearch, cleanAnswers as quizCleanAnswers, searchWithAnswers as quizSearchWithAnswers } from './toolQuizzes.js';

export const BOOKING_URL = 'https://cal.com/linustse/meeting-with-linus?overlayCalendar=true';
/** The white paper's landing page. The PDF itself is at WHITE_PAPER_PDF; readers get it after leaving an email. */
export const WHITE_PAPER_URL = '/white-papers/ecommerce-data-stack';
export const WHITE_PAPER_PDF = '/downloads/ecommerce-data-stack-white-paper.pdf';
export const REASON_MIN_POINTS = 2;
export const MAX_REASONS = 2;

/** Layers in the order they are decided. `display` is the order on the result card. */
const LAYERS = [
  {
    id: 'approach',
    label: 'Approach',
    optional: false,
    order: ['packaged-first', 'hybrid', 'warehouse-build'],
    picks: {
      'packaged-first': {
        name: 'Start with a packaged tool, and plan the warehouse',
        summary: 'A packaged analytics tool answers your questions today for less than a build. The layers below are the stack to build when its definitions or sources stop fitting.',
        fallbackReasons: ['Packaged tools get you dashboards in days, built on standard ecommerce definitions.', 'Nothing in your answers needs a model of your own yet.'],
      },
      hybrid: {
        name: 'Build a warehouse, and keep a packaged tool where it helps',
        summary: 'The warehouse holds the numbers that have to tie out across channels. A packaged tool keeps doing the daily job it is good at, and its data can load into the warehouse too.',
        fallbackReasons: ['Many brands run both: a packaged tool for daily views and a warehouse for anything that has to reconcile.', 'Your answers point to some reporting a packaged model will not cover.'],
      },
      'warehouse-build': {
        name: 'Build a warehouse you own',
        summary: 'Load every channel into one warehouse, model orders, margin, marketing, and customers to your own definitions, and report from there. It is a project, so it should replace manual work you can name.',
        fallbackReasons: ['Your channels and rules need a model you own.', 'A packaged tool would leave part of your reporting in spreadsheets.'],
        disclosure: 'Building warehouses is the work Greyfield Data does, so weigh this result with that in mind. Ties go to the packaged route.',
      },
    },
  },
  {
    id: 'packaged',
    label: 'Packaged analytics',
    optional: true,
    order: ['none', 'lifetimely', 'triple-whale', 'peel', 'polar', 'daasity'],
    picks: {
      none: {
        name: 'None needed for now',
        summary: 'The warehouse and BI layers cover your reporting. Add a packaged tool later only for a job it does better, such as pixel attribution.',
        fallbackReasons: ['Your approach is a warehouse build, so a second set of definitions would compete with yours.', 'You can still load an attribution tool into the warehouse later if marketing wants one.'],
        requires: [{ layers: { approach: ['warehouse-build'] } }],
      },
      lifetimely: {
        name: 'Lifetimely',
        summary: 'Daily profit and LTV for a Shopify store, with a free plan and pricing by order count.',
        link: '/compare/lifetimely-vs-custom-data-warehouse',
        fallbackReasons: ['Lifetimely gives a Shopify store daily profit and LTV without a project.', 'It is the lightest packaged option on this list.'],
        requires: [{ layers: { approach: ['packaged-first', 'hybrid'] }, answers: { channels: ['shopify', 'shopify-amazon'] } }],
      },
      'triple-whale': {
        name: 'Triple Whale',
        summary: 'Pixel attribution and dashboards for Shopify-first brands. Its data can sync to Snowflake or BigQuery through a paid add-on.',
        link: '/compare/triple-whale-vs-custom-data-warehouse',
        fallbackReasons: ['Triple Whale ships a tracking pixel and attribution models a warehouse build does not.', 'Marketing can keep it while the warehouse handles reconciled numbers.'],
        requires: [{ layers: { approach: ['packaged-first', 'hybrid'] } }],
      },
      peel: {
        name: 'Peel',
        summary: 'Retention, cohorts, and subscription LTV for Shopify and Amazon, with Snowflake data access on every plan.',
        link: '/compare/peel-vs-custom-data-warehouse',
        fallbackReasons: ['Peel answers cohort and LTV questions out of the box.', 'It reads Shopify and Amazon, and includes Snowflake data access.'],
        requires: [{ layers: { approach: ['packaged-first', 'hybrid'] } }],
      },
      polar: {
        name: 'Polar Analytics',
        summary: 'Shopify-led BI with predefined ecommerce metrics, a pixel, and a dedicated Snowflake database on every plan.',
        link: '/compare/polar-analytics-vs-custom-data-warehouse',
        fallbackReasons: ['Polar gives a Shopify-led brand a warehouse without a data team.', 'Its predefined metrics cover most standard reporting.'],
        requires: [{ layers: { approach: ['packaged-first', 'hybrid'] } }],
      },
      daasity: {
        name: 'Daasity',
        summary: 'Prebuilt omnichannel commerce models in Snowflake or BigQuery, with retail and syndicated data connectors.',
        link: '/compare/daasity-vs-custom-data-warehouse',
        fallbackReasons: ['Daasity models DTC and retail data together without a build of your own.', 'Its models land in a warehouse you can query with SQL.'],
        requires: [{ layers: { approach: ['packaged-first', 'hybrid'] } }],
      },
    },
  },
  {
    id: 'ingestion',
    label: 'Ingestion',
    optional: false,
    order: ['fivetran', 'airbyte', 'daton', 'funnel'],
    picks: {
      fivetran: {
        name: 'Fivetran',
        summary: 'Managed connectors with nothing to host. The broadest catalog of the four, and the one to check first when sources go beyond ecommerce.',
        fallbackReasons: ['It has the broadest connector catalog of the four, which matters as sources grow.', 'It covers storefront, ad, and back-office sources in one tool.'],
      },
      airbyte: {
        name: 'Airbyte',
        summary: 'Open-source connectors you can self-host, or run on Airbyte\'s cloud. Lower tool cost, more engineering time.',
        fallbackReasons: ['Airbyte trades a lower bill for engineering time to run it.', 'Its open-source connectors can be extended when a source is missing.'],
      },
      daton: {
        name: 'Daton',
        summary: 'Low-cost ELT built for ecommerce and marketplace sources, loading into your own warehouse.',
        link: '/compare/saras-analytics-vs-custom-data-warehouse',
        fallbackReasons: ['Daton is built for ecommerce, marketplace, and ad sources.', 'It loads into a warehouse you own, so it can sit under a custom build.'],
      },
      funnel: {
        name: 'Funnel',
        summary: 'A marketing data hub that harmonizes many ad and analytics platforms. Snowflake export is on its Enterprise plan, and it does not ship a profit model.',
        link: '/compare/funnel-vs-custom-data-warehouse',
        fallbackReasons: ['Funnel is strongest at cleaning up data from many ad platforms.', 'Your answers put marketing data first.'],
      },
    },
  },
  {
    id: 'warehouse',
    label: 'Warehouse',
    optional: false,
    order: ['bigquery', 'snowflake', 'databricks'],
    picks: {
      bigquery: {
        name: 'BigQuery',
        summary: 'Google\'s serverless warehouse. The natural fit when you already use Google Cloud, GA4, or Looker Studio.',
        fallbackReasons: ['BigQuery needs no cluster sizing and suits a first warehouse.', 'It sits next to GA4 and Looker Studio in Google Cloud.'],
      },
      snowflake: {
        name: 'Snowflake',
        summary: 'A cloud warehouse on AWS, Azure, or Google Cloud, widely supported by ecommerce connectors and packaged tools.',
        fallbackReasons: ['Polar, Peel, Daasity, and Triple Whale\'s warehouse sync all support Snowflake, so a packaged tool can feed it later.', 'It runs on AWS, Azure, or Google Cloud.'],
      },
      databricks: {
        name: 'Databricks',
        summary: 'A lakehouse platform. Choose it when your company already runs on Databricks, not as a first warehouse for reporting.',
        fallbackReasons: ['You already run Databricks, so a second platform would add cost and work.', 'Your data team can model ecommerce data alongside its other work there.'],
        requires: [{ answers: { cloud: ['databricks'] } }],
      },
    },
  },
  {
    id: 'transformation',
    label: 'Transformation',
    optional: false,
    order: ['dbt-platform', 'dbt-core', 'vendor-models'],
    picks: {
      'dbt-platform': {
        name: 'dbt on dbt Labs\' managed platform',
        summary: 'SQL models for orders, fees, refunds, and customers, with scheduling, docs, and tests hosted for you.',
        fallbackReasons: ['dbt keeps every definition in version-controlled SQL with tests.', 'The managed platform saves running a scheduler yourself.'],
      },
      'dbt-core': {
        name: 'dbt Core',
        summary: 'The open-source dbt, run on your own scheduler. Same models, less tool cost, more for your team to operate.',
        fallbackReasons: ['dbt Core is free and keeps definitions in version-controlled SQL.', 'Your team can run the scheduler itself.'],
      },
      'vendor-models': {
        name: 'Daasity\'s prebuilt models, extended with your own SQL',
        summary: 'Start from the vendor\'s omnichannel models and add the rules that are specific to you.',
        link: '/compare/daasity-vs-custom-data-warehouse',
        fallbackReasons: ['Prebuilt models save writing standard commerce logic from scratch.', 'You can still add your own SQL for rules the models do not cover.'],
        requires: [{ layers: { packaged: ['daasity'] } }],
      },
    },
  },
  {
    id: 'bi',
    label: 'BI',
    optional: false,
    order: ['looker-studio', 'metabase', 'power-bi', 'sigma'],
    picks: {
      'looker-studio': {
        name: 'Looker Studio',
        summary: 'Google\'s free dashboards. Enough for a founder or marketing scorecard on modeled tables.',
        fallbackReasons: ['Looker Studio costs nothing and reads BigQuery and many other sources directly.', 'Your answers favor simple scorecards over self-serve analysis.'],
      },
      metabase: {
        name: 'Metabase',
        summary: 'Open-source BI with a free self-hosted edition. Cheap, simple dashboards for a team comfortable with SQL.',
        link: '/compare/sigma-vs-metabase',
        fallbackReasons: ['Metabase keeps BI cost low for a team that writes SQL.', 'It can be self-hosted.'],
      },
      'power-bi': {
        name: 'Power BI',
        summary: 'Microsoft\'s BI tool. Hard to beat on price and fit when you run on Microsoft 365 and finance lives in Excel.',
        link: '/compare/sigma-vs-power-bi',
        fallbackReasons: ['Power BI fits a Microsoft company on price and familiarity.', 'Finance can keep working next to Excel.'],
      },
      sigma: {
        name: 'Sigma',
        summary: 'A spreadsheet interface on live warehouse data, with input tables for typing plans and adjustments back into the warehouse.',
        link: '/compare/sigma-vs-power-bi',
        fallbackReasons: ['Sigma lets spreadsheet users work on live warehouse data.', 'Input tables let finance and ops enter plans next to actuals.'],
        disclosure: 'Greyfield Data sells Sigma consulting. Sigma is listed last when scores tie.',
      },
    },
  },
  {
    id: 'semantic',
    label: 'Semantic layer',
    optional: true,
    order: ['dictionary', 'semantic-views', 'bi-model'],
    picks: {
      dictionary: {
        name: 'A metric dictionary in your dbt project',
        summary: 'One written entry per metric with its grain, date, sources, owner, and tie-out check, built as dbt marts every tool reads.',
        link: '/blog/ecommerce-semantic-layer',
        fallbackReasons: ['A written dictionary is the cheapest semantic layer, and every later option builds on it.', 'It stops "revenue" meaning four things in four reports.'],
      },
      'semantic-views': {
        name: 'Snowflake semantic views, on top of the dictionary',
        summary: 'Define metrics and joins once inside Snowflake so BI and AI tools query the same definitions.',
        link: '/blog/snowflake-semantic-views-guide',
        fallbackReasons: ['You are on Snowflake, so the definitions can live in the warehouse itself.', 'AI and BI tools then read the same metrics.'],
        requires: [{ layers: { warehouse: ['snowflake'] } }],
      },
      'bi-model': {
        name: 'The Power BI semantic model, fed by the dictionary',
        summary: 'Keep measures in one shared Power BI model that every report connects to, with the dictionary as its spec.',
        link: '/compare/sigma-vs-power-bi',
        fallbackReasons: ['Your reports live in Power BI, so its shared model is the natural place for measures.', 'One shared model stops each report defining revenue again.'],
        requires: [{ layers: { bi: ['power-bi'] } }],
      },
    },
  },
  {
    id: 'activation',
    label: 'Activation',
    optional: true,
    order: ['later', 'reverse-etl'],
    picks: {
      later: {
        name: 'Not yet: use your tools\' native syncs',
        summary: 'Shopify, Klaviyo, and your ad platforms already share basic audiences. Add warehouse activation after the customer view in Phase 3.',
        fallbackReasons: ['Native integrations cover basic audiences without another tool.', 'Warehouse audiences only pay off once the customer view is trusted.'],
      },
      'reverse-etl': {
        name: 'Hightouch or Census',
        summary: 'Push warehouse audiences and scores back to Klaviyo, Meta, and other tools, so targeting uses the same definitions as reporting. Plan it for Phase 4.',
        fallbackReasons: ['Reverse ETL sends warehouse segments to the tools that act on them.', 'Targeting then matches the numbers in your reports.'],
        requires: [{ layers: { approach: ['hybrid', 'warehouse-build'] } }],
      },
    },
  },
];

export const DISPLAY_ORDER = ['ingestion', 'warehouse', 'transformation', 'semantic', 'bi', 'activation', 'packaged'];

export const PHASES = Object.freeze({
  'phase-0': { id: 'phase-0', number: 0, name: 'Phase 0: audit and definitions', summary: 'List every source, write the metric dictionary, and pick a closed month every number must tie to before anything is built.' },
  'phase-1': { id: 'phase-1', number: 1, name: 'Phase 1: orders and margin', summary: 'One order table across channels, with refunds, fees, and product costs, that ties to the books for a closed month.' },
  'phase-2': { id: 'phase-2', number: 2, name: 'Phase 2: marketing and CAC', summary: 'Spend by channel next to new customers, with blended and attributed CAC defined once and contribution after marketing.' },
  'phase-3': { id: 'phase-3', number: 3, name: 'Phase 3: retention and customer view', summary: 'One ID per person across channels, cohorts that only count customers who have had time to return, and one customer timeline.' },
  'phase-4': { id: 'phase-4', number: 4, name: 'Phase 4: forecasting and activation', summary: 'Demand and inventory planning on trusted history, and warehouse audiences sent back to marketing tools.' },
});

const Q = (id, label, text, options) => ({ id, label, text, options });

export const STACK_QUIZ = Object.freeze({
  id: 'stack',
  path: '/tools/ecommerce-data-stack-quiz',
  layers: LAYERS,
  questions: [
    Q('channels', 'Sales channels', 'Where do you sell?', [
      { value: 'shopify', label: 'Shopify only', hint: 'One store, or a few on Shopify.', reason: 'You sell on Shopify only, so one store holds every order.',
        points: { approach: { 'packaged-first': 4, 'warehouse-build': -3 }, packaged: { lifetimely: 2, polar: 2, 'triple-whale': 1, peel: 1, daasity: -4 }, ingestion: { fivetran: 1, airbyte: 1, daton: -1 } },
        reasons: { 'packaged.polar': 'You sell on Shopify only, which is what Polar\'s predefined model is built around.', 'packaged.lifetimely': 'You sell on Shopify only, and Lifetimely reads the store directly.' } },
      { value: 'shopify-amazon', label: 'Shopify and Amazon', hint: 'Both are a real share of revenue.', reason: 'You sell on Shopify and Amazon, so every layer has to read both channels.',
        points: { approach: { 'packaged-first': 1, hybrid: 2, 'warehouse-build': 1 }, packaged: { peel: 3, polar: 1, daasity: 1, lifetimely: -1 }, ingestion: { daton: 2, fivetran: 1 } },
        reasons: { 'packaged.peel': 'You sell on Shopify and Amazon, and Peel reads both.', 'ingestion.daton': 'You sell on Amazon, and Daton is built for marketplace sources like the SP-API.' } },
      { value: 'marketplaces', label: 'Shopify, Amazon, and TikTok Shop', hint: 'Orders arrive from three or more marketplaces.', reason: 'Orders arrive from Shopify, Amazon, and TikTok Shop, each with its own fees and dates.',
        points: { approach: { 'packaged-first': -2, hybrid: 2, 'warehouse-build': 3 }, packaged: { polar: 1, daasity: 2, peel: -1 }, ingestion: { daton: 3, fivetran: 1 } },
        reasons: { 'packaged.daasity': 'You sell in several places, and Daasity\'s prebuilt omnichannel models are built for that.', 'ingestion.daton': 'You sell on Amazon and TikTok Shop, which ecommerce-focused connectors like Daton target.', 'approach.warehouse-build': 'Three marketplaces means one order and margin model across channels, without double counting.' } },
      { value: 'omnichannel', label: 'Online plus wholesale or retail', hint: 'Faire, retailers, a POS, or an ERP.', reason: 'You sell online and through wholesale or retail, so ERP and retailer data sit next to online orders.',
        points: { approach: { 'packaged-first': -4, hybrid: 2, 'warehouse-build': 4 }, packaged: { daasity: 5 }, ingestion: { fivetran: 2, airbyte: 1, daton: 1 }, transformation: { 'vendor-models': 1 }, warehouse: { snowflake: 1 }, bi: { sigma: 1, 'power-bi': 1 } },
        reasons: { 'packaged.daasity': 'You sell into retail, and Daasity has retail and syndicated data connectors.', 'ingestion.fivetran': 'Wholesale and ERP sources go beyond ecommerce apps, where Fivetran\'s broader catalog helps. Plan custom code for retailer files no connector covers.' } },
    ]),
    Q('revenue', 'Annual revenue', 'What is your annual revenue?', [
      { value: 'under-1m', label: 'Under $1M', reason: 'At under $1M a year, setup speed and a low bill matter more than owning the model.',
        points: { approach: { 'packaged-first': 4, hybrid: -1, 'warehouse-build': -3 }, packaged: { lifetimely: 3 }, ingestion: { airbyte: 1 }, transformation: { 'dbt-core': 1 }, bi: { 'looker-studio': 2, metabase: 1 } } },
      { value: '1-10m', label: '$1M to $10M', reason: 'At $1M to $10M, a focused paid tool usually pays for itself before a data project would.',
        points: { approach: { 'packaged-first': 2, hybrid: 1 }, packaged: { 'triple-whale': 1, polar: 1, peel: 1 }, bi: { 'looker-studio': 1 } } },
      { value: '10-50m', label: '$10M to $50M', reason: 'At $10M to $50M, numbers that disagree start to cost more than a proper model.',
        points: { approach: { 'packaged-first': -1, hybrid: 2, 'warehouse-build': 1 }, packaged: { polar: 1, daasity: 1 }, ingestion: { fivetran: 1 }, transformation: { 'dbt-platform': 1 } } },
      { value: 'over-50m', label: 'Over $50M', reason: 'Above $50M, owning and auditing the data model usually matters more than setup speed.',
        points: { approach: { 'packaged-first': -3, hybrid: 1, 'warehouse-build': 3 }, ingestion: { fivetran: 1 }, warehouse: { snowflake: 1 }, transformation: { 'dbt-platform': 1 }, bi: { sigma: 1, 'power-bi': 1 } } },
    ]),
    Q('today', 'Reporting today', 'What do you report from today?', [
      { value: 'platform-reports', label: 'Platform reports and spreadsheets', hint: 'Shopify, Amazon, and ad platform exports.', reason: 'You report from platform exports today, so a packaged tool would be a big step up on its own.',
        points: { approach: { 'packaged-first': 2 } } },
      { value: 'packaged', label: 'A packaged analytics tool', hint: 'Triple Whale, Polar, Peel, or similar.', reason: 'You already use a packaged tool, and it can keep its job while a warehouse takes the rest.',
        points: { approach: { 'packaged-first': 1, hybrid: 3 } } },
      { value: 'raw-warehouse', label: 'A warehouse with raw data loaded', hint: 'Connectors run, but little is modeled.', reason: 'You already load raw data into a warehouse, so the next step is modeling it.',
        points: { approach: { 'packaged-first': -2, hybrid: 1, 'warehouse-build': 2 } } },
      { value: 'modeled', label: 'Modeled orders and margin in a warehouse', hint: 'Finance trusts the order numbers.', reason: 'You already have modeled orders and margin, so the stack builds on what works.',
        points: { approach: { 'packaged-first': -3, 'warehouse-build': 3 } } },
    ]),
    Q('agree', 'Agreement on revenue', 'Do finance, marketing, and Shopify agree on last month\'s revenue?', [
      { value: 'yes', label: 'Yes, or we know exactly why they differ', reason: 'Your teams already agree on revenue.',
        points: { approach: { 'packaged-first': 1 }, semantic: { dictionary: 1 } } },
      { value: 'roughly', label: 'Roughly, within a few points', reason: 'Your teams roughly agree on revenue, but the gaps are not written down.',
        points: { semantic: { dictionary: 2 } } },
      { value: 'no', label: 'No, or nobody has checked', reason: 'Your teams do not agree on revenue yet, so definitions come before tools.',
        points: { approach: { 'packaged-first': -1, hybrid: 1, 'warehouse-build': 1 }, semantic: { dictionary: 3 } },
        reasons: { 'semantic.dictionary': 'Your teams do not agree on revenue, and a written dictionary is how that stops.' } },
    ]),
    Q('focus', 'Main question', 'What is the main question you need answered?', [
      { value: 'profit', label: 'What we actually make on each order', reason: 'Your main question is profit after every fee.',
        points: { approach: { hybrid: 1, 'warehouse-build': 1 }, packaged: { lifetimely: 2, polar: 1, daasity: 1 }, ingestion: { daton: 1 }, semantic: { dictionary: 1 } },
        reasons: { 'packaged.lifetimely': 'Your main question is profit, and Lifetimely is built around daily profit and LTV.' } },
      { value: 'marketing', label: 'Which ads and channels are working', reason: 'Your main question is which ads and channels are working.',
        points: { approach: { 'packaged-first': 1, hybrid: 1 }, packaged: { 'triple-whale': 5 }, ingestion: { funnel: 3, fivetran: 1 }, bi: { 'looker-studio': 1 } },
        reasons: { 'packaged.triple-whale': 'Your main question is which ads are working, and Triple Whale\'s pixel and attribution are built for it.', 'ingestion.funnel': 'Your main question is marketing performance, and Funnel is strongest at harmonizing ad platform data.' } },
      { value: 'retention', label: 'Who comes back, and what they are worth', reason: 'Your main question is retention and customer value.',
        points: { approach: { 'packaged-first': 1, hybrid: 1 }, packaged: { peel: 5, lifetimely: 1 }, activation: { 'reverse-etl': 1 } },
        reasons: { 'packaged.peel': 'Your main question is retention, and Peel ships cohorts, LTV, and repeat purchase reports out of the box.' } },
      { value: 'forecasting', label: 'What to reorder, and what demand will be', reason: 'Your main question is demand and inventory planning across channels.',
        points: { approach: { 'warehouse-build': 2 }, packaged: { daasity: 1 }, ingestion: { fivetran: 1 }, bi: { sigma: 2, 'power-bi': 1 } },
        reasons: { 'bi.sigma': 'You plan demand and inventory, and Sigma input tables let planners type forecasts next to actuals.' } },
    ]),
    Q('users', 'Report users', 'Who will use the reports most?', [
      { value: 'founders', label: 'Founders, mostly', hint: 'A daily check on sales and spend.', reason: 'The reports are mainly for founders, so a simple scorecard beats a flexible tool.',
        points: { approach: { 'packaged-first': 1 }, packaged: { polar: 1, lifetimely: 1 }, bi: { 'looker-studio': 2 } } },
      { value: 'marketing', label: 'The marketing team', hint: 'Campaigns, creative, and channel spend.', reason: 'The marketing team is the main audience.',
        points: { packaged: { 'triple-whale': 2 }, ingestion: { funnel: 2 }, bi: { 'looker-studio': 2 } },
        reasons: { 'packaged.triple-whale': 'The marketing team is the main audience, and Triple Whale is built around its daily questions.' } },
      { value: 'finance', label: 'Finance and leadership', hint: 'Numbers that have to tie to the books.', reason: 'Finance needs numbers that tie to the books.',
        points: { approach: { 'packaged-first': -1, hybrid: 2, 'warehouse-build': 1 }, bi: { sigma: 3, 'power-bi': 2 }, semantic: { dictionary: 1, 'bi-model': 1 } },
        reasons: { 'bi.sigma': 'Finance is the main audience, and Sigma gives them a spreadsheet on live warehouse data.', 'bi.power-bi': 'Finance is the main audience, and Power BI sits next to Excel.' } },
      { value: 'ops', label: 'Operations and merchandising', hint: 'Stock, purchasing, and product.', reason: 'Operations and merchandising need stock and sales in one place.',
        points: { packaged: { daasity: 1 }, bi: { sigma: 2, 'power-bi': 1, metabase: 1 } } },
    ]),
    Q('team', 'Data team', 'Who will own the data work?', [
      { value: 'none', label: 'Nobody, and we do not plan to hire', reason: 'Nobody in house will own data models, so every layer has to run itself.',
        points: { approach: { 'packaged-first': 3, hybrid: -1, 'warehouse-build': -3 }, packaged: { polar: 2 }, ingestion: { fivetran: 2, daton: 1, airbyte: -3 }, transformation: { 'dbt-platform': 2, 'vendor-models': 2, 'dbt-core': -3 }, bi: { 'looker-studio': 1, metabase: -2 }, semantic: { 'bi-model': 2 } },
        reasons: { 'ingestion.fivetran': 'Nobody will run infrastructure, and Fivetran maintains its connectors for you.', 'packaged.polar': 'Nobody will own data work, and Polar includes a managed warehouse without a data team.' } },
      { value: 'partner', label: 'An agency or consultant', reason: 'An outside partner will build and look after the models.',
        points: { approach: { hybrid: 1, 'warehouse-build': 1 }, ingestion: { fivetran: 1, daton: 1 }, transformation: { 'dbt-platform': 2 } },
        reasons: { 'transformation.dbt-platform': 'A partner will build the models, and a managed dbt project is easy to hand over.' } },
      { value: 'analyst', label: 'One analyst who writes SQL', reason: 'You have an analyst who can own SQL models.',
        points: { approach: { hybrid: 1, 'warehouse-build': 1 }, transformation: { 'dbt-platform': 1, 'dbt-core': 1 }, bi: { metabase: 2 }, semantic: { dictionary: 1 } },
        reasons: { 'bi.metabase': 'Your analyst writes SQL, and Metabase is a low-cost place to publish it.' } },
      { value: 'data-team', label: 'A data team with engineers', reason: 'You have engineers who can run open-source tools and own the models.',
        points: { approach: { 'packaged-first': -2, 'warehouse-build': 3 }, ingestion: { airbyte: 3 }, transformation: { 'dbt-core': 3 }, semantic: { 'semantic-views': 2 }, bi: { metabase: 1 } },
        reasons: { 'ingestion.airbyte': 'You have engineers, so self-hosted Airbyte trades their time for a lower bill.', 'transformation.dbt-core': 'Your engineers can run dbt Core on your own scheduler.' } },
    ]),
    Q('cloud', 'Cloud and tools', 'Which platform does the company already use?', [
      { value: 'google', label: 'Google Workspace or Google Cloud', reason: 'You already use Google\'s tools.',
        points: { warehouse: { bigquery: 5 }, bi: { 'looker-studio': 3 } },
        reasons: { 'warehouse.bigquery': 'You already use Google Cloud, so BigQuery is billed and managed where you are.', 'bi.looker-studio': 'You use Google\'s tools, and Looker Studio reads BigQuery natively.' } },
      { value: 'microsoft', label: 'Microsoft 365 or Azure', reason: 'You run on Microsoft 365 or Azure.',
        points: { warehouse: { snowflake: 3 }, bi: { 'power-bi': 5 }, semantic: { 'bi-model': 2 } },
        reasons: { 'warehouse.snowflake': 'You run on Microsoft, and Snowflake runs on Azure.', 'bi.power-bi': 'You run on Microsoft 365, where Power BI is the native choice.', 'semantic.bi-model': 'You run on Microsoft, and a shared Power BI model is where measures already live.' } },
      { value: 'snowflake', label: 'We already have Snowflake', reason: 'You already have Snowflake.',
        points: { warehouse: { snowflake: 6 }, bi: { sigma: 2 }, semantic: { 'semantic-views': 2 } },
        reasons: { 'warehouse.snowflake': 'You already have Snowflake, so there is nothing new to buy.', 'bi.sigma': 'You are on Snowflake, which Sigma queries live.', 'semantic.semantic-views': 'You are on Snowflake, so metrics can be defined in semantic views inside the warehouse.' } },
      { value: 'databricks', label: 'We already have Databricks', reason: 'You already run Databricks.',
        points: { warehouse: { databricks: 6 }, bi: { sigma: 1, 'power-bi': 1 } },
        reasons: { 'warehouse.databricks': 'You already run Databricks, so a second platform would add cost and work.' } },
      { value: 'none', label: 'No preference', reason: 'You have no platform preference, so the choice comes down to the tools around it.',
        points: { warehouse: { bigquery: 2, snowflake: 2 } } },
    ]),
    Q('spend', 'Spending style', 'How do you prefer to spend on data tools?', [
      { value: 'lean', label: 'Lowest tool bills, even if it takes more of our time', reason: 'You want the lowest tool bills.',
        points: { approach: { 'packaged-first': 1 }, ingestion: { airbyte: 2, daton: 2, fivetran: -1, funnel: -1 }, transformation: { 'dbt-core': 2, 'dbt-platform': -1 }, bi: { 'looker-studio': 2, metabase: 2, sigma: -2 } },
        reasons: { 'ingestion.daton': 'You want the lowest connector bill, and Daton is positioned as low-cost, ecommerce-focused ELT.', 'ingestion.airbyte': 'You want the lowest bill, and Airbyte\'s open-source edition is free to self-host.', 'transformation.dbt-core': 'You want the lowest bill, and dbt Core is free.', 'bi.looker-studio': 'You want the lowest bill, and Looker Studio is free.', 'bi.metabase': 'You want the lowest bill, and Metabase has a free self-hosted edition.' } },
      { value: 'balanced', label: 'Balanced: pay for what saves real time', reason: 'You pay for tools where they save real time.',
        points: { ingestion: { daton: 1, fivetran: 1 }, transformation: { 'dbt-platform': 1 }, bi: { 'power-bi': 1 } } },
      { value: 'managed', label: 'Managed tools, so the team stays small', reason: 'You would rather pay for managed tools than grow the team.',
        points: { ingestion: { fivetran: 3, funnel: 1, airbyte: -1 }, transformation: { 'dbt-platform': 2 }, bi: { sigma: 2 }, packaged: { polar: 1 } },
        reasons: { 'ingestion.fivetran': 'You prefer managed tools, and Fivetran runs and maintains connectors for you.', 'transformation.dbt-platform': 'You prefer managed tools, and dbt Labs hosts scheduling, docs, and tests.', 'bi.sigma': 'You prefer managed tools, and Sigma needs no server and no extracts to maintain.' } },
    ]),
    Q('activation', 'Activation', 'Do you want warehouse audiences sent back to Klaviyo, Meta, or other tools?', [
      { value: 'now', label: 'Yes, soon', hint: 'Segments and scores built on our own definitions.', reason: 'You want warehouse segments in your marketing tools.',
        points: { approach: { hybrid: 1 }, activation: { 'reverse-etl': 5 } },
        reasons: { 'activation.reverse-etl': 'You want segments built on your own definitions in Klaviyo and ad platforms, which is what reverse ETL does.' } },
      { value: 'later', label: 'Later, once reporting is solid', reason: 'Activation can wait until reporting is solid.',
        points: { activation: { later: 2 } } },
      { value: 'no', label: 'No, native integrations are enough', reason: 'Native integrations between your tools are enough for now.',
        points: { activation: { later: 4 } } },
    ]),
  ],
});

function own(object, key) {
  return object !== null && typeof object === 'object' && Object.prototype.hasOwnProperty.call(object, key);
}

function findOption(question, value) {
  if (typeof value !== 'string') return null;
  return question.options.find((option) => option.value === value) || null;
}

export function cleanAnswers(answers) {
  return quizCleanAnswers(STACK_QUIZ, answers);
}

function optionPoints(option, layerId, pickId) {
  if (!own(option.points, layerId)) return 0;
  const layer = option.points[layerId];
  const value = own(layer, pickId) ? Number(layer[pickId]) : 0;
  return Number.isFinite(value) ? value : 0;
}

function allowed(pick, chosen, answers) {
  if (!pick.requires) return true;
  return pick.requires.some((condition) => {
    for (const [layerId, ids] of Object.entries(condition.layers || {})) if (!ids.includes(chosen[layerId])) return false;
    for (const [questionId, values] of Object.entries(condition.answers || {})) if (!values.includes(answers[questionId])) return false;
    return true;
  });
}

function reasonsFor(layer, pickId, answers) {
  const pick = layer.picks[pickId];
  const found = [];
  STACK_QUIZ.questions.forEach((question, index) => {
    const option = findOption(question, answers[question.id]);
    if (!option) return;
    const value = optionPoints(option, layer.id, pickId);
    if (value < REASON_MIN_POINTS) return;
    const key = `${layer.id}.${pickId}`;
    const text = own(option.reasons, key) ? option.reasons[key] : option.reason;
    if (text) found.push({ text, value, index });
  });
  found.sort((a, b) => b.value - a.value || a.index - b.index);
  const reasons = [];
  for (const entry of found) {
    if (reasons.length >= MAX_REASONS) break;
    if (!reasons.includes(entry.text)) reasons.push(entry.text);
  }
  for (const fallback of pick.fallbackReasons) {
    if (reasons.length >= MAX_REASONS) break;
    if (!reasons.includes(fallback)) reasons.push(fallback);
  }
  return reasons;
}

/** Rank one layer's picks for the answers; picks whose requirements fail are left out. */
export function rankLayer(layerId, answers, chosen = {}) {
  return rankClean(layerId, cleanAnswers(answers), chosen);
}

function rankClean(layerId, clean, chosen) {
  const layer = LAYERS.find((entry) => entry.id === layerId);
  if (!layer) return [];
  const totals = [];
  layer.order.forEach((pickId, index) => {
    const pick = layer.picks[pickId];
    if (!allowed(pick, chosen, clean)) return;
    let total = 0;
    for (const question of STACK_QUIZ.questions) {
      const option = findOption(question, clean[question.id]);
      if (option) total += optionPoints(option, layerId, pickId);
    }
    totals.push({ id: pickId, name: pick.name, points: total, index });
  });
  totals.sort((a, b) => b.points - a.points || a.index - b.index);
  return totals.map(({ id, name, points }) => ({ id, name, points }));
}

/** Which phase to start with. Rules, not points, so the logic is easy to read and test. */
export function startPhase(answers) {
  return phaseFor(cleanAnswers(answers));
}

function phaseFor(a) {
  const reasons = [];
  let id;
  if (a.agree === 'no') {
    id = 'phase-0';
    reasons.push('Your teams do not agree on revenue yet, and no tool fixes that until the definitions are written down.');
  } else if (a.today === 'platform-reports' || a.today === 'packaged') {
    if (a.agree === 'yes') {
      id = 'phase-1';
      reasons.push('Your teams already agree on revenue, so you can go straight to loading orders, refunds, and fees.');
    } else {
      id = 'phase-0';
      reasons.push('Nothing is in a warehouse yet and the revenue gaps are not written down, so start with the source inventory and dictionary.');
    }
  } else if (a.today === 'raw-warehouse') {
    id = 'phase-1';
    reasons.push('Raw data is already loading, so the next job is one order table that ties to the books.');
  } else if (a.focus === 'retention') {
    id = 'phase-3';
    reasons.push('Orders and margin are already modeled, and your main question is retention.');
  } else if (a.focus === 'forecasting' || a.activation === 'now') {
    id = 'phase-4';
    reasons.push(a.focus === 'forecasting' ? 'Orders and margin are modeled, so demand planning has trusted history to work from.' : 'Orders and margin are modeled, and you want audiences sent back to marketing tools.');
    reasons.push('Check that marketing (Phase 2) and the customer view (Phase 3) are in place first, since forecasting and activation read from both.');
  } else {
    id = 'phase-2';
    reasons.push('Orders and margin are already modeled, so marketing spend and CAC come next.');
  }
  if (reasons.length < 2) reasons.push('Each phase ends with acceptance checks, so you only move on once its numbers tie out.');
  return { ...PHASES[id], reasons };
}

export function score(answers) {
  const clean = cleanAnswers(answers);
  const answered = Object.keys(clean).length;
  const total = STACK_QUIZ.questions.length;
  const complete = answered === total;
  if (!complete) return { answered, total, complete, approach: null, layers: [], phase: null };
  const chosen = {};
  const decided = {};
  for (const layer of LAYERS) {
    const ranking = rankClean(layer.id, clean, chosen);
    const [winner, second] = ranking;
    chosen[layer.id] = winner.id;
    const pick = layer.picks[winner.id];
    decided[layer.id] = {
      layer: layer.id,
      label: layer.label,
      optional: layer.optional,
      id: winner.id,
      name: pick.name,
      summary: pick.summary,
      points: winner.points,
      link: pick.link || null,
      disclosure: pick.disclosure || null,
      reasons: reasonsFor(layer, winner.id, clean),
      runnerUp: second ? { id: second.id, name: second.name, points: second.points } : null,
      ranking,
    };
  }
  const notes = [];
  if (clean.channels === 'omnichannel') notes.push('Plan for some custom code: retailer portals, EDI files, and wholesale spreadsheets often have no connector.');
  if (clean.channels === 'marketplaces' || clean.channels === 'shopify-amazon') notes.push('Check that your connector loads the Amazon settlement and fee reports you need, not only orders.');
  return {
    answered,
    total,
    complete,
    approach: decided.approach,
    layers: DISPLAY_ORDER.map((id) => decided[id]),
    phase: phaseFor(clean),
    ingestionNotes: notes,
  };
}

export function answersFromSearch(search) {
  return quizAnswersFromSearch(STACK_QUIZ, search);
}

export function searchWithAnswers(answers, existing = '') {
  return quizSearchWithAnswers(STACK_QUIZ, answers, existing);
}
